/**
 * Cleanup: We previously registered an Omnisend push notification service
 * worker here. It caused a visible reload on mobile devices the first time it
 * took control of the page. Push notifications have been removed entirely.
 *
 * This module now only ensures that any leftover service worker registered by
 * a previous version of the app is silently unregistered on the next visit.
 *
 * Performance: We mark the cleanup as done in localStorage after the first run
 * so subsequent page loads don't even touch navigator.serviceWorker. This
 * keeps the homepage fast for returning visitors.
 */

const CLEANUP_FLAG = 'sw-cleanup-v1-done';

export function registerPushServiceWorker(): void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

  // Ignore the admin area – nothing to clean up there.
  if (/^\/admin/i.test(location.pathname)) return;

  // Already cleaned up on this device → skip entirely.
  try {
    if (localStorage.getItem(CLEANUP_FLAG) === '1') return;
  } catch (_) { /* ignore storage errors */ }

  // Run after load so we never block the critical render path.
  const cleanup = () => {
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => Promise.all(regs.map((r) => r.unregister().catch(() => false))))
      .then(() => {
        try { localStorage.setItem(CLEANUP_FLAG, '1'); } catch (_) { /* ignore */ }
      })
      .catch(() => {});
  };

  if (document.readyState === 'complete') {
    window.setTimeout(cleanup, 2500);
  } else {
    window.addEventListener('load', () => window.setTimeout(cleanup, 2500), { once: true });
  }
}
